import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import {
  Badge, Banner, EmptyState, PageLoading, SearchInput, Select, TextInput,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import { money } from './orderMeta';
import { exportXlsx } from '../reports/reportExport';

import type { CustomerType, PriceList, PriceListDetail, ReportColumn } from '../../types';

/**
 * Price lists.
 *
 * The screen is built around the number in the corner: **how many
 * products are not priced on this list.** A list that looks full but
 * silently hides 760 products from whoever is assigned to it is the
 * failure this module has, and it is invisible unless something counts
 * it and says so.
 *
 * Prices are edited in place and saved in a batch, because that is how
 * an admin works: open the list, change eleven numbers, save.
 */
export default function PriceListsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [lists, setLists] = useState<PriceList[] | null>(null);
  const [types, setTypes] = useState<CustomerType[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<PriceListDetail | null>(null);
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [managingTypes, setManagingTypes] = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadLists = useCallback(async () => {
    try {
      const data = await api.get<PriceList[]>('/orders/price-lists');
      setLists(data);
      setSelected((current) => (
        current && data.some((l) => l.id === current)
          ? current
          : data.find((l) => l.isDefault)?.id ?? data[0]?.id ?? null
      ));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load price lists.');
    }
  }, [toast]);

  const loadTypes = useCallback(async () => {
    try {
      setTypes(await api.get<CustomerType[]>('/customers/types'));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load customer types.');
    }
  }, [toast]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      setDetail(await api.get<PriceListDetail>(`/orders/price-lists/${id}`));
      setEdits({});
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load that list.');
    }
  }, [toast]);

  useEffect(() => { void loadLists(); }, [loadLists]);
  useEffect(() => { void loadTypes(); }, [loadTypes]);
  useEffect(() => { if (selected) void loadDetail(selected); }, [selected, loadDetail]);

  const items = useMemo(() => {
    const all = detail?.items ?? [];
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      (i) => i.name.toLowerCase().includes(needle) || i.code.toLowerCase().includes(needle),
    );
  }, [detail, search]);

  const dirty = Object.keys(edits).length > 0;

  async function save() {
    if (!selected || !dirty) return;
    setBusy(true);
    try {
      const prices = Object.entries(edits).map(([productId, value]) => ({
        productId,
        price: value.trim() === '' ? null : Number(value).toFixed(2),
      }));
      const result = await api.put<{ set: number; removed: number }>(
        `/orders/price-lists/${selected}/prices`, { prices },
      );
      toast.success(
        result.removed
          ? `${result.set} priced, ${result.removed} removed from this list.`
          : `${result.set} price${result.set === 1 ? '' : 's'} saved.`,
      );
      await loadDetail(selected);
      await loadLists();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save those prices.');
    } finally {
      setBusy(false);
    }
  }

  if (!lists) {
    return (
      <>
        <PageHeader title="Price lists" />
        <div className="page"><PageLoading /></div>
      </>
    );
  }

  const current = lists.find((l) => l.id === selected) ?? null;

  return (
    <>
      <PageHeader
        title="Price lists"
        subtitle="What each kind of customer pays. Anyone unassigned buys on the default list."
        actions={isAdmin ? (
          <>
            {dirty ? (
              <button type="button" className="btn btn-primary" disabled={busy}
                onClick={() => void save()}>
                Save {Object.keys(edits).length} change{Object.keys(edits).length === 1 ? '' : 's'}
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-ghost"
                  disabled={!detail?.items.length} onClick={() => void exportList()}>
                  <Icon name="download" size={16} /> Export
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => fileRef.current?.click()}>
                  <Icon name="download" size={16} /> Import
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setAddingProduct(true)}>
                  <Icon name="plus" size={16} /> Add product
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setManagingTypes(true)}>
                  <Icon name="users" size={16} /> Manage types
                </button>
                <button type="button" className="btn btn-ghost"
                  disabled={!current?.productCount} onClick={() => setAdjusting(true)}>
                  <Icon name="edit" size={16} /> Revise all
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                  <Icon name="plus" size={16} /> New list
                </button>
              </>
            )}
          </>
        ) : undefined}
      />

      <div className="page col gap-16">
        <div className="tabs">
          {lists.map((list) => (
            <button
              key={list.id}
              type="button"
              className={`tab${list.id === selected ? ' active' : ''}`}
              onClick={() => setSelected(list.id)}
            >
              {list.name}
              {list.isDefault ? <Badge tone="brand">Default</Badge> : null}
            </button>
          ))}
        </div>

        {detail ? (
          <>
            {detail.unpriced > 0 ? (
              <Banner kind="warning">
                <strong>
                  {detail.unpriced} active product{detail.unpriced === 1 ? '' : 's'} on this
                  list {detail.unpriced === 1 ? 'has' : 'have'} no price.
                </strong>
                <div style={{ marginTop: 4 }}>
                  A customer on {detail.name} cannot be sold {detail.unpriced === 1 ? 'it' : 'them'} —
                  reps do not see {detail.unpriced === 1 ? 'it' : 'them'} at all. That is
                  deliberate: a blank price would otherwise become a zero on a real order.
                </div>
              </Banner>
            ) : null}

            <div className="toolbar between">
              <SearchInput value={search} onChange={setSearch} placeholder="Find a product" />
              <div className="row gap-12 small faint">
                <span>{detail.productCount ?? 0} priced</span>
                <span>·</span>
                <span>{detail.customerCount ?? 0} customer{detail.customerCount === 1 ? '' : 's'}</span>
                <span>·</span>
                {isAdmin ? (
                  <label className="row gap-8" style={{ alignItems: 'center' }}>
                    <span>Serves</span>
                    <select
                      className="select"
                      style={{ width: 160, minHeight: 34 }}
                      value={detail.customerType?.id ?? ''}
                      aria-label="Serves customer type"
                      onChange={(e) => void changeType(detail.id, e.target.value || null)}
                    >
                      <option value="">— No type —</option>
                      {types.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <span>Serves {detail.customerType?.name ?? 'no type'}</span>
                )}
                {isAdmin && !detail.isDefault ? (
                  <>
                    <span>·</span>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => void makeDefault(detail.id)}>
                      Make default
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="card">
              {items.length === 0 ? (
                <EmptyState
                  icon="list"
                  title={search ? 'Nothing matches that' : 'No products yet'}
                  body={search ? undefined : 'Add products first, then price them here.'}
                />
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Product</th>
                        <th>Unit</th>
                        <th className="num">MRP</th>
                        <th className="num">GST</th>
                        <th className="num" style={{ width: 150 }}>Price on this list</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const edited = edits[item.productId];
                        const value = edited !== undefined
                          ? edited
                          : (item.price == null ? '' : String(item.price));
                        return (
                          <tr key={item.productId} className={item.isActive ? undefined : 'faint'}>
                            <td className="mono small nowrap">{item.code}</td>
                            <td className="primary">
                              {item.name}
                              {!item.isActive ? <> <Badge tone="neutral">Inactive</Badge></> : null}
                            </td>
                            <td>{item.unit}</td>
                            <td className="num">{item.mrp == null ? '—' : money(item.mrp)}</td>
                            <td className="num">{item.gstRate}%</td>
                            <td className="num">
                              <input
                                className={`input price-input${
                                  edited !== undefined ? ' edited' : ''}`}
                                type="number"
                                step="0.01"
                                min="0"
                                disabled={!isAdmin}
                                placeholder="Not sold"
                                value={value}
                                onChange={(e) => setEdits((current) => ({
                                  ...current, [item.productId]: e.target.value,
                                }))}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {isAdmin ? (
              <p className="small faint">
                Clear a price to stop selling that product on this list. Nothing is saved
                until you press Save.
              </p>
            ) : null}
          </>
        ) : <PageLoading />}
      </div>

      {creating ? (
        <NewListDialog
          lists={lists}
          types={types}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); setSelected(id); void loadLists(); }}
        />
      ) : null}

      {managingTypes ? (
        <ManageTypesModal
          types={types}
          onClose={() => setManagingTypes(false)}
          onChanged={() => { void loadTypes(); void loadLists(); if (selected) void loadDetail(selected); }}
        />
      ) : null}

      {adjusting && current ? (
        <AdjustDialog
          list={current}
          onClose={() => setAdjusting(false)}
          onDone={() => { setAdjusting(false); void loadDetail(current.id); }}
        />
      ) : null}

      {addingProduct ? (
        <AddProductModal
          onClose={() => setAddingProduct(false)}
          onCreated={() => {
            setAddingProduct(false);
            if (selected) void loadDetail(selected);
            void loadLists();
          }}
        />
      ) : null}

      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onImportFile(f);
          e.target.value = '';
        }}
      />
    </>
  );

  // Download this list as Excel: Code, Product, Unit, MRP, GST, Price.
  // Numeric columns stay numeric so the sheet can be edited and re-imported.
  async function exportList() {
    if (!detail) return;
    const cols: ReportColumn[] = [
      { key: 'code', label: 'Code', group: '', type: 'text' },
      { key: 'name', label: 'Product', group: '', type: 'text' },
      { key: 'unit', label: 'Unit', group: '', type: 'text' },
      { key: 'mrp', label: 'MRP', group: '', type: 'number' },
      { key: 'gstRate', label: 'GST %', group: '', type: 'number' },
      { key: 'price', label: 'Price', group: '', type: 'number' },
    ];
    const rows = detail.items.map((i) => ({
      code: i.code, name: i.name, unit: i.unit,
      mrp: i.mrp ?? '', gstRate: i.gstRate, price: i.price ?? '',
    }));
    await exportXlsx(`pricelist_${current?.name ?? 'list'}`, cols, rows);
  }

  // Read an Excel sheet of Code + Price and stage the matching prices as
  // pending edits — the admin reviews them and presses Save, so nothing is
  // written silently. A blank price clears it (stops selling that product).
  async function onImportFile(file: File) {
    if (!detail) return;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const ws = sheetName ? wb.Sheets[sheetName] : undefined;
      if (!ws) { toast.error('That Excel file has no sheets.'); return; }
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
      const byCode = new Map(detail.items.map((i) => [i.code.trim().toLowerCase(), i.productId]));
      const pick = (r: Record<string, unknown>, keys: string[]) => {
        for (const k of Object.keys(r)) {
          if (keys.includes(k.trim().toLowerCase())) return r[k];
        }
        return '';
      };
      const next: Record<string, string> = {};
      let matched = 0;
      let unknown = 0;
      for (const r of rows) {
        const code = String(pick(r, ['code'])).trim();
        if (!code) continue;
        const pid = byCode.get(code.toLowerCase());
        if (!pid) { unknown += 1; continue; }
        next[pid] = String(pick(r, ['price', 'price on this list'])).trim();
        matched += 1;
      }
      if (matched === 0) {
        toast.error('No matching product codes found. Check the Code column.');
        return;
      }
      setEdits((cur) => ({ ...cur, ...next }));
      toast.success(
        `${matched} price${matched === 1 ? '' : 's'} loaded`
        + (unknown ? `, ${unknown} unknown code${unknown === 1 ? '' : 's'} skipped` : '')
        + '. Review and press Save.',
      );
    } catch {
      toast.error('Could not read that Excel file.');
    }
  }

  async function makeDefault(id: string) {
    try {
      await api.patch(`/orders/price-lists/${id}`, { isDefault: true });
      toast.success('Unassigned customers now buy on this list.');
      await loadLists();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not change the default.');
    }
  }

  async function changeType(id: string, customerTypeId: string | null) {
    try {
      await api.patch(`/orders/price-lists/${id}`, { customerTypeId });
      toast.success(customerTypeId
        ? 'Customers of that type now buy on this list.'
        : 'This list no longer serves a customer type.');
      await loadLists();
      await loadDetail(id);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'TYPE_ALREADY_ATTACHED') {
        toast.error(err.message);
        await loadLists();
        await loadDetail(id);
        return;
      }
      toast.error(err instanceof ApiClientError ? err.message : 'Could not change the type.');
    }
  }
}

// =====================================================================

function NewListDialog({
  lists, types, onClose, onCreated,
}: {
  lists: PriceList[]; types: CustomerType[];
  onClose: () => void; onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [copyFromId, setCopyFromId] = useState(
    lists.find((l) => l.isDefault)?.id ?? lists[0]?.id ?? '',
  );
  const [customerTypeId, setCustomerTypeId] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const created = await api.post<PriceList>('/orders/price-lists', {
        name: name.trim(),
        copyFromId: copyFromId || undefined,
        customerTypeId: customerTypeId || undefined,
      });
      toast.success(`${created.name} created.`);
      onCreated(created.id);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not create that list.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New price list"
      subtitle="A segment that pays differently — wholesale, institutional, a key account."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim()}
            onClick={() => void create()}>
            Create
          </button>
        </>
      )}
    >
      <div className="col gap-4">
        <TextInput
          label="Name"
          value={name}
          autoFocus
          placeholder="Wholesale"
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          label="Start from"
          value={copyFromId}
          hint="Copies every price across, so you adjust rather than key 800 numbers."
          onChange={(e) => setCopyFromId(e.target.value)}
        >
          {lists.map((l) => (
            <option key={l.id} value={l.id}>{l.name} ({l.productCount ?? 0} prices)</option>
          ))}
          <option value="">Nothing — start empty</option>
        </Select>
        <Select
          label="Serves customer type"
          value={customerTypeId}
          hint="The type of customer this list prices for. A type can back only one list."
          onChange={(e) => setCustomerTypeId(e.target.value)}
        >
          <option value="">— None —</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}

function AdjustDialog({
  list, onClose, onDone,
}: { list: PriceList; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [percent, setPercent] = useState('5');
  const [roundTo, setRoundTo] = useState('0.5');
  const [preview, setPreview] = useState<
    { count: number; sample: { code: string; name: string; from: number; to: number }[] } | null
  >(null);
  const [busy, setBusy] = useState(false);

  async function run(isPreview: boolean) {
    setBusy(true);
    try {
      const result = await api.post<{
        preview: boolean; count?: number; adjusted?: number;
        sample?: { code: string; name: string; from: number; to: number }[];
      }>(`/orders/price-lists/${list.id}/adjust`, {
        percent: Number(percent),
        roundTo: Number(roundTo),
        preview: isPreview,
      });

      if (isPreview) {
        setPreview({ count: result.count ?? 0, sample: result.sample ?? [] });
      } else {
        toast.success(`${result.adjusted} prices revised.`);
        onDone();
      }
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not revise those prices.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Revise every price on ${list.name}`}
      subtitle="The annual increase, in one operation rather than a spreadsheet."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {preview ? (
            <button type="button" className="btn btn-primary" disabled={busy}
              onClick={() => void run(false)}>
              Apply to {preview.count} prices
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={busy}
              onClick={() => void run(true)}>
              Preview
            </button>
          )}
        </>
      )}
    >
      <div className="col gap-4">
        <div className="row gap-12">
          <div style={{ flex: 1 }}>
            <TextInput
              label="Change by"
              type="number"
              step="0.5"
              value={percent}
              hint="Negative to reduce"
              onChange={(e) => { setPercent(e.target.value); setPreview(null); }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Select
              label="Round to"
              value={roundTo}
              onChange={(e) => { setRoundTo(e.target.value); setPreview(null); }}
            >
              <option value="0.01">The paisa</option>
              <option value="0.5">50 paise</option>
              <option value="1">The rupee</option>
              <option value="5">5 rupees</option>
            </Select>
          </div>
        </div>

        {preview ? (
          <div className="card">
            <div className="card-head">
              <h3>What changes</h3>
              <span className="small faint">
                First {preview.sample.length} of {preview.count}
              </span>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Product</th><th className="num">Now</th><th className="num">Becomes</th></tr>
                </thead>
                <tbody>
                  {preview.sample.map((row) => (
                    <tr key={row.code}>
                      <td>{row.name}</td>
                      <td className="num faint">{money(row.from)}</td>
                      <td className="num primary">{money(row.to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <Banner kind="info">
            Orders already booked keep the prices they were booked at — this only
            changes what the next order will cost.
          </Banner>
        )}
      </div>
    </Modal>
  );
}

// =====================================================================

function ManageTypesModal({
  types, onClose, onChanged,
}: { types: CustomerType[]; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  async function add() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post<CustomerType>('/customers/types', { name });
      toast.success(`${name} added.`);
      setNewName('');
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not add that type.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    try {
      await api.del(`/customers/types/${id}`);
      toast.success('Type removed.');
      setConfirmId(null);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that type.');
    } finally {
      setBusy(false);
    }
  }

  const confirming = confirmId ? types.find((t) => t.id === confirmId) ?? null : null;

  return (
    <Modal
      title="Customer types"
      subtitle="The segments a customer can belong to. Each can back one price list."
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Done</button>}
    >
      <div className="col gap-12">
        {types.length === 0 ? (
          <EmptyState
            icon="users"
            title="No customer types yet"
            body="Add one below — Retail, Wholesale, Distributor — then attach a price list to it."
          />
        ) : (
          types.map((type) => (
            <TypeRow
              key={type.id}
              type={type}
              onRenamed={onChanged}
              onDelete={() => setConfirmId(type.id)}
            />
          ))
        )}

        <div className="row gap-8" style={{ alignItems: 'flex-end' }}>
          <div className="grow">
            <TextInput
              label="Add a type"
              value={newName}
              placeholder="Wholesale"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
            />
          </div>
          <button type="button" className="btn btn-primary" disabled={busy || !newName.trim()}
            onClick={() => void add()}>
            <Icon name="plus" size={16} /> Add
          </button>
        </div>
      </div>

      {confirming ? (
        <Modal
          title={`Remove ${confirming.name}?`}
          onClose={() => setConfirmId(null)}
          dismissable={false}
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmId(null)}>
                Keep it
              </button>
              <button type="button" className="btn btn-danger" disabled={busy}
                onClick={() => void remove(confirming.id)}>
                {busy ? <span className="spinner" /> : null}
                Remove type
              </button>
            </>
          )}
        >
          <Banner kind="warning">
            Any customers of this type lose it, and any price list serving it is detached.
            Nothing else about those customers or lists changes.
          </Banner>
        </Modal>
      ) : null}
    </Modal>
  );
}

function TypeRow({
  type, onRenamed, onDelete,
}: { type: CustomerType; onRenamed: () => void; onDelete: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(type.name);
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== '' && name.trim() !== type.name;

  async function save() {
    if (!dirty) return;
    setBusy(true);
    try {
      await api.patch<CustomerType>(`/customers/types/${type.id}`, { name: name.trim() });
      toast.success('Renamed.');
      onRenamed();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not rename that type.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row gap-8" style={{ alignItems: 'center' }}>
      <div className="grow">
        <input
          className="input"
          value={name}
          aria-label={`Rename ${type.name}`}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }}
        />
      </div>
      <span className="small faint nowrap">
        {type.customerCount ?? 0} customer{type.customerCount === 1 ? '' : 's'}
      </span>
      <button type="button" className="btn btn-secondary btn-sm" disabled={!dirty || busy}
        onClick={() => void save()}>
        Save
      </button>
      <button type="button" className="btn btn-danger-ghost btn-sm" disabled={busy}
        aria-label={`Remove ${type.name}`} onClick={onDelete}>
        <Icon name="trash" size={15} />
      </button>
    </div>
  );
}

// =====================================================================

/**
 * Create a catalogue product without leaving the price list. It lands as
 * an active product, unpriced on every list until someone prices it —
 * which is exactly why the "add product" button lives here.
 */
function AddProductModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: '', name: '', unit: 'piece', gstRate: '', mrp: '', hsnCode: '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function create() {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('A code and a name are required.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/orders/products', {
        code: form.code.trim(),
        name: form.name.trim(),
        unit: form.unit.trim() || 'piece',
        gstRate: form.gstRate.trim() === '' ? 0 : Number(form.gstRate),
        ...(form.mrp.trim() === '' ? {} : { mrp: Number(form.mrp) }),
        ...(form.hsnCode.trim() === '' ? {} : { hsnCode: form.hsnCode.trim() }),
      });
      toast.success(`${form.name.trim()} added. Price it on the lists that sell it.`);
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not add that product.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add product"
      subtitle="It lands active and unpriced on every list until you price it."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <span className="grow" />
          <button type="button" className="btn btn-primary" disabled={busy}
            onClick={() => void create()}>
            Add product
          </button>
        </>
      )}
    >
      <div className="col gap-12">
        <div className="row gap-12 wrap">
          <TextInput label="Code" value={form.code} autoFocus onChange={set('code')} />
          <TextInput label="Unit" hint="piece, box, kg…" value={form.unit} onChange={set('unit')} />
        </div>
        <TextInput label="Product name" value={form.name} onChange={set('name')} />
        <div className="row gap-12 wrap">
          <TextInput label="MRP" type="number" value={form.mrp} onChange={set('mrp')} />
          <TextInput label="GST %" type="number" value={form.gstRate} onChange={set('gstRate')} />
          <TextInput label="HSN code" value={form.hsnCode} onChange={set('hsnCode')} />
        </div>
      </div>
    </Modal>
  );
}
